import React, { ReactElement, useCallback, useEffect, useState } from "react";
import cn from "classnames";
import { useFormikContext } from "formik";
import { triggerSnowplowModalActionEvent, triggerSnowplowClickEvent } from "@themuse/snowplow-js-client";
import { getScreenByStep } from "../../constants";
import { submitJobApply } from "../../DirectApplyUtils";
import { ProgressBar } from "../../../ProgressBar/ProgressBar";
import SquareButton from "../../../Buttons/SquareButton";
import { ArrowLeftShape, IconLoading } from "../../../Icons";
import styles from "./Footer.module.scss";
import { JobCategory, TrackingObject } from "../../DirectApplyTypes";

interface BackBtnProps {
    label: string;
    cb: () => void;
}

/**
 * Generate the back button, can be null.
 */
const BackBtn = (props: BackBtnProps): ReactElement | null => {
    const { label, cb } = props;
    return label
        ? (
            <button
                className={styles.backButton}
                onClick={cb}
            >
                <ArrowLeftShape />{label}
            </button>
        ) : null;
}

interface NextBtnProps {
    cb: () => Promise<void>;
    isSubmitting: boolean;
    label: string;
    step: number;
    steps: number;
}

/**
 * Generate the next button, can be null.
 * @returns {ReactElement} A button.
 */
const NextBtn = (props: NextBtnProps): ReactElement | null => {
    const {
        cb,
        isSubmitting,
        label,
        step,
        steps,
    } = props;
    let modalAction;
    const { errors, values, setTouched, touched } = useFormikContext();
    const screen = getScreenByStep(step);
    const valuesObj = (values as Record<string, string>);
    const errorsObj = (errors as Record<string, string>);
    const [disabled, setDisabled] = useState<boolean>(true);

    /**
     * This function sets the tracking and action props when the button is clicked.
     * Setting these directly causes a race condition between the update and the reading of the attributes by Snowplow.
     */
    const footerNextClick = () => {
        modalAction = "modal_action";
        const nextTouched = screen.required.reduce((accum, x) => ({ ...accum, [x]: true }), touched);
        setTouched(nextTouched)
        if (!disabled) {
            cb();
        }
    };

    useEffect(() => {
        const invalid = screen.validate.map((x: string) => (errorsObj as any)[x]).some(Boolean);
        const blanks = screen.required.map((x: string) => !(valuesObj as any)[x]).some(Boolean);
        setDisabled(blanks || invalid);
    }, [screen, errorsObj, valuesObj]);

    return label
        ? (
            <SquareButton
                color="secondary-100"
                type={step === steps - 1 ? "submit" : "button"}
                data-tracking-event-type={modalAction}
                className={cn(styles.forwardButton, {
                    [styles.forwardButtonSubmitting]: isSubmitting,
                })}
                onClick={footerNextClick}
            >
                {label}
                {isSubmitting && <IconLoading />}
            </SquareButton>
        ) : null;
};

interface FooterProps {
    endpoint: string;
    link: string;
    jobId?: number;
    jobCategory: JobCategory | null;
    normalizedTitle: string;
    level: string,
    step: number;
    stepBack: () => void;
    stepForward: () => void;
    steps: number;
    snowplowId: string;
    companyId: string;
    atsJobId: string;
    signUpCallBack: (props: any) => Promise<boolean>;
    service: string;
    onClose: () => void;
    trackingProps: TrackingObject;
    showMoreJobsButton?: boolean;
}

/**
 * Generates a footer component for a form, which includes a progress bar and two buttons (back and next). The function
 * also handles the logic for disabling the next/back button based on the form's validation and current step.
 *
 * @returns {ReactElement} The footer section of a form, including the progress bar and navigation buttons.
 */
const Footer = (props: FooterProps): ReactElement => {
    const {
        endpoint,
        jobId,
        jobCategory,
        normalizedTitle,
        level,
        link,
        step,
        stepBack,
        stepForward,
        steps,
        signUpCallBack,
        snowplowId,
        companyId,
        atsJobId,
        service,
        onClose,
        trackingProps,
        showMoreJobsButton = false
    } = props;
    const { values } = useFormikContext();
    const valuesObj = (values as Record<string, string>);
    const screen = getScreenByStep(step);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const applyProps = {
        link,
        endpoint,
        values: valuesObj,
        resumeName: valuesObj.resumeName,
        coverLetterName: valuesObj.coverLetterName,
        snowplowId,
        companyId,
        atsJobId,
        jobId,
        service
    };

    const stepBackMemoized = useCallback(() => {
        triggerSnowplowModalActionEvent({
            ...trackingProps,
            sp_modal_action_type: "back",
        });
        stepBack();
    }, [stepBack, trackingProps]);

    const subscribeValues = {
        alerts: valuesObj.alerts,
        newsletter: valuesObj.newsletter,
        location: valuesObj.location,
        email: valuesObj.email,
        page_section: "job_detail",
        category: jobCategory?.code ?? "",
        keyword: normalizedTitle,
        jobId,
        level
    };

    /**
     * Mutates the form state to apply previous field changes and advance to the next screen.
     * Performs additional actions based on what screen you are on.
     */
    const chooseYourOwnAdventure = async () => {
        const handleReviewApplication = async () => {
            setIsSubmitting(true);
            const response = await submitJobApply(applyProps).catch(console.error);
            triggerSnowplowModalActionEvent({
                ...trackingProps,
                sp_modal_action_type: screen.btnLabel,
                sp_modal_error_type: !response ? "Job Apply was unsuccessful." : null,
                sp_tags: [{ type: "direct_apply_vendor", value: service }]
            });
            setIsSubmitting(false);
        };

        const handleDirectApplyEmailRegistration = async () => {
            if ([valuesObj.alerts, valuesObj.newsletter].some((x) => x === "true")) {
                setIsSubmitting(true);
                const response = await signUpCallBack(subscribeValues).catch(console.error);
                // This is the only time we should send the actual subscriber values. Everywhere else they are null.
                triggerSnowplowModalActionEvent({
                    ...trackingProps,
                    sp_modal_action_type: screen.btnLabel,
                    sp_modal_job_alert_opt_in: valuesObj.alerts === "true",
                    sp_modal_newsletter_opt_in: valuesObj.newsletter === "true",
                    sp_modal_error_type: !response ? "Subscription was unsuccessful." : null
                });
                setIsSubmitting(false);
            } else {
                triggerSnowplowModalActionEvent({
                    ...trackingProps,
                    sp_modal_action_type: screen.btnLabel,
                });
            }
        };

        const handleApplyConfirmation = () => {
            triggerSnowplowModalActionEvent({
                ...trackingProps,
                sp_modal_action_type: screen.btnLabel,
            });
            onClose();
        };

        const handleDefaultCase = () => {
            triggerSnowplowModalActionEvent({
                ...trackingProps,
                sp_modal_action_type: screen.btnLabel,
            });
        };

        switch (screen.name) {
            case "review_application":
                await handleReviewApplication();
                break;
            case "direct_apply_email_registration":
                await handleDirectApplyEmailRegistration();
                break;
            case "apply_confirmation":
                handleApplyConfirmation();
                break;
            default:
                handleDefaultCase();
                break;
        }
        stepForward();
    };

    const handleMoreJobsClick = async () => {
        triggerSnowplowClickEvent({
            sp_click_component: "more_jobs_button",
            sp_click_position: "main",
            sp_click_target_page_section: "search",
            sp_click_button_text: `Search for more jobs${jobCategory?.value && ` in ${jobCategory?.value}`}`,
            sp_click_button_type: "more_jobs",
            sp_tile_type: '',
            sp_tile_entity_id: ''
        });
        if(jobCategory?.code) {
            const url = `https://www.themuse.com/search/category/${jobCategory?.code}`;
            return window.open(url, '_self');
        }
        const url = `https://www.themuse.com/search`;
        return window.open(url, '_self');
    }

    const isMoreJobsButtonVisible = screen.name === "apply_confirmation" && showMoreJobsButton;
    return (
        <footer className={styles.footer}>
            {screen.name !== "apply_confirmation"
                ? (
                    <ProgressBar {...{
                        step: step,
                        maxSteps: steps,
                        withSteps: true
                    }} />
                ) : (
                    null
                )}
            <div className={styles.footerButtons}>
                {/* we can never go beyond step 1 and once we are at the finish line, it is too late. */}
                {![1, steps].includes(step) && <BackBtn  {...{ label: "BACK", cb: stepBackMemoized }} />}
                
                <div className={styles.forwardButtonContainer}>
                    {isMoreJobsButtonVisible && ( 
                        <SquareButton color="light-blue" type={"button"} className={styles.moreJobsButton} onClick={handleMoreJobsClick}>
                            {"Search for more jobs"} {jobCategory?.value && `in ${jobCategory?.value}`}
                        </SquareButton>
                    )}
                    <NextBtn {...{ label: screen.btnLabel, step, steps, cb: chooseYourOwnAdventure, isSubmitting }} />
                </div>
            </div>
        </footer>
    );
};
Footer.displayName = "Footer";

export default Footer;
